import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'

type StarsBackgroundProps = {
  speed?: number
  starColor?: string
  className?: string
}

function generateStars(count: number, starColor: string) {
  const shadows: string[] = []
  for (let i = 0; i < count; i++) {
    const x = Math.floor(Math.random() * 4000) - 2000
    const y = Math.floor(Math.random() * 4000) - 2000
    shadows.push(`${x}px ${y}px ${starColor}`)
  }
  return shadows.join(', ')
}

export default function StarsBackground({
  speed = 50,
  starColor = '#fff',
  className,
}: StarsBackgroundProps) {
  const [boxShadow1, setBoxShadow1] = useState('')
  const [boxShadow2, setBoxShadow2] = useState('')
  const [boxShadow3, setBoxShadow3] = useState('')

  // Inject keyframes for vertical scroll once
  useEffect(() => {
    const id = 'stars-scroll-keyframes'
    if (!document.getElementById(id)) {
      const style = document.createElement('style')
      style.id = id
      style.textContent = `
        @keyframes stars-scroll-up {
          from { transform: translateY(0); }
          to { transform: translateY(-2000px); }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  useEffect(() => {
    setBoxShadow1(generateStars(1000, starColor))
    setBoxShadow2(generateStars(400, starColor))
    setBoxShadow3(generateStars(200, starColor))
  }, [starColor])

  const layerStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '2000px',
      animationName: 'stars-scroll-up',
      animationTimingFunction: 'linear',
      animationIterationCount: 'infinite',
    }),
    [],
  )

  return (
    <div
      className={cn(
        'relative w-full h-full overflow-hidden bg-[radial-gradient(ellipse_at_bottom,#262626_0%,#000_100%)]',
        className,
      )}
    >
      {/* Star Layer 1 */}
      <div
        style={{ ...layerStyle, animationDuration: `${speed}s` }}
      >
        <div
          className="absolute bg-transparent rounded-full"
          style={{ width: '1px', height: '1px', boxShadow: boxShadow1 }}
        />
        <div
          className="absolute bg-transparent rounded-full top-500"
          style={{ width: '1px', height: '1px', boxShadow: boxShadow1 }}
        />
      </div>

      {/* Star Layer 2 */}
      <div
        style={{ ...layerStyle, animationDuration: `${speed * 2}s` }}
      >
        <div
          className="absolute bg-transparent rounded-full"
          style={{ width: '2px', height: '2px', boxShadow: boxShadow2 }}
        />
        <div
          className="absolute bg-transparent rounded-full top-500"
          style={{ width: '2px', height: '2px', boxShadow: boxShadow2 }}
        />
      </div>

      {/* Star Layer 3 */}
      <div
        style={{ ...layerStyle, animationDuration: `${speed * 3}s` }}
      >
        <div
          className="absolute bg-transparent rounded-full"
          style={{ width: '3px', height: '3px', boxShadow: boxShadow3 }}
        />
        <div
          className="absolute bg-transparent rounded-full top-500"
          style={{ width: '3px', height: '3px', boxShadow: boxShadow3 }}
        />
      </div>
    </div>
  )
}

